import React, { useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DownOutlined } from '@ant-design/icons';
import { ActionPlan } from '../ActionPlan';
import { RiskEvaluation } from '../RiskEvaluation';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useGetRiskFactorCategories } from './hooks/useGetRiskFactorCategories';
import { RiskBadge } from '@/components/views/Resolution886/components/RiskBadge';
import { TableCategoryQuestionsReview } from '../../../../TableCategoryQuestionsReview';

import * as S from './styles';

export function RiskFactorCategories(): JSX.Element {
	const [categoryKey, setCategoryKey] = useState<string | null>(null);
	const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
	const [loadingCategories, setLoadingCategories] = useState<Record<string, boolean>>({});

	const { report, handleSetSelectedAssessmentId } = useResolution886Report();
	const { tasks, isLoading, refetchCategoryResults } = useGetRiskFactorCategories(report, categoryKey || undefined);

	function toggleDropdown(
		categoryId: string,
		categoryKey: string,
		categoryResultId: string | null,
		assessmentId: string
	) {
		const isOpening = !openDropdowns[categoryId];

		if (assessmentId) {
			handleSetSelectedAssessmentId(assessmentId);
		}

		setOpenDropdowns((prev) => ({
			[categoryId]: !prev[categoryId]
		}));

		if (isOpening) {
			setLoadingCategories((prev) => ({
				...prev,
				[categoryId]: true
			}));

			if (categoryResultId) {
				setCategoryKey(categoryKey);
				refetchCategoryResults().finally(() => {
					setLoadingCategories((prev) => ({
						...prev,
						[categoryId]: false
					}));
				});
			} else {
				setLoadingCategories((prev) => ({
					...prev,
					[categoryId]: false
				}));
			}
		}
	}

	if (isLoading && tasks.length === 0) {
		return (
			<div style={{ padding: '20px', paddingTop: '0' }}>
				<div style={{ marginBottom: '30px' }}>
					<Skeleton.Input active size="large" style={{ width: '200px', marginBottom: '16px' }} />
					<div style={{ backgroundColor: 'white', padding: '16px' }}>
						{[1, 2, 3].map((index) => (
							<div key={index} style={{ marginBottom: index < 3 ? '16px' : '0' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Skeleton.Input active size="default" style={{ width: '150px' }} />
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<Skeleton.Button
											active
											size="small"
											style={{ width: '60px', height: '20px' }}
										/>
										<Skeleton.Button
											active
											size="small"
											style={{ width: '16px', height: '16px' }}
										/>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ padding: '20px', paddingTop: '0' }}>
			<Row style={{ marginBottom: '16px' }}>
				<Col span={24}>
					<S.TaskTitle>{I18n.get('Spreadsheet 2: Initial risk factor evaluations')}</S.TaskTitle>
				</Col>
			</Row>
			{tasks.map((task) => (
				<div key={task.id} style={{ marginBottom: '30px' }}>
					<S.TaskName>{task.name}</S.TaskName>
					<div style={{ backgroundColor: 'white' }}>
						{task.categories.map((category: any, index: number) => (
							<div key={category.id}>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										padding: '12px 0',
										borderBottom: index < task.categories.length - 1 ? '1px solid #E0E0E0' : 'none',
										cursor: 'pointer'
									}}
									onClick={() =>
										toggleDropdown(
											category.id,
											category.key,
											category.category_result_id,
											task.assessmentId
										)
									}
								>
									<S.CategoryName>{category.name}</S.CategoryName>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<RiskBadge risk={category.riskLevel} />
										<DownOutlined style={{ color: '#666', fontSize: '14px' }} />
									</div>
								</div>

								{openDropdowns[category.id] && (
									<div
										style={{
											padding: '16px 0',
											backgroundColor: 'white'
										}}
									>
										{loadingCategories[category.id] ? (
											<div>
												<Skeleton.Input
													active
													size="default"
													style={{ width: '100%', height: '40px' }}
												/>
											</div>
										) : (
											<>
												<TableCategoryQuestionsReview
													exposure_time={category.exposure_time}
													first_step={category.first_step}
													second_step={category.second_step}
												/>
												{category.risk_evaluation && (
													<RiskEvaluation
														data={category.risk_evaluation}
														isLoading={loadingCategories[category.id]}
													/>
												)}
												{category.action_plan && <ActionPlan data={category.action_plan} />}
											</>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}
